import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { toast, Toaster } from "sonner";
import { BarChart3, TrendingUp, Users, ChevronDown, Filter } from "lucide-react";
import AdminSubnav from "@/components/app/AdminSubnav";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area,
} from "recharts";

const CATEGORY_LABELS = {
  golf: "Golf",
  promotion: "Promotion",
  representation_compagnie: "Représentation - Compagnie",
  representation_repas: "Représentation - Repas",
  association: "Association",
  hotel_avion: "Hôtel / Avion",
  auto_essence: "Auto / Essence",
  stationnement: "Stationnement / Taxi",
  salon_congres: "Salon / Congrès",
  bureau: "Bureau",
  informatique: "Informatique",
  communication: "Communication",
  formation: "Formation",
};

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = month ? { month } : {};
      const { data } = await api.get("/admin/stats", { params });
      setStats(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const maxUser = useMemo(() => Math.max(1, ...(stats?.by_user || []).map((u) => u.total)), [stats]);
  const maxCat = useMemo(() => Math.max(1, ...(stats?.by_category || []).map((c) => c.total)), [stats]);
  const maxMonth = useMemo(() => Math.max(1, ...(stats?.by_month || []).map((m) => m.total)), [stats]);

  // Prepare chart data (reverse for chronological ascending)
  const monthChart = useMemo(() => {
    const arr = [...(stats?.by_month || [])];
    arr.reverse();
    return arr.map((m) => ({ month: m.month, total: m.total, count: m.count }));
  }, [stats]);

  const categoryChart = useMemo(() => {
    return (stats?.by_category || []).slice(0, 10).map((c) => ({
      name: (CATEGORY_LABELS[c.category] || c.category).replace("Frais de ", "").replace("Représentation - ", "Rep. "),
      total: c.total,
    }));
  }, [stats]);

  const userChart = useMemo(() => {
    return (stats?.by_user || []).slice(0, 8).map((u) => ({
      name: u.name.length > 18 ? u.name.slice(0, 16) + "…" : u.name,
      total: u.total,
    }));
  }, [stats]);

  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const axisColor = isDark ? "#9CA3AF" : "#6B7280";
  const gridColor = isDark ? "#1F2635" : "#E5E7EB";
  const tooltipBg = isDark ? "#151A26" : "#FFFFFF";
  const tooltipBorder = isDark ? "#1F2635" : "#E5E7EB";

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <AdminSubnav />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-2">Administration · Statistiques globales</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none">
            Vue d'ensemble.
          </h1>
          <p className="text-gray-500 mt-3 max-w-lg">Dépenses consolidées de tous les utilisateurs — par utilisateur, par catégorie, par mois.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="overline flex items-center gap-1"><Filter size={12} /> Mois</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="ra-input"
            data-testid="stats-month-filter"
          />
          {month && (
            <button onClick={() => setMonth("")} className="btn-ghost" data-testid="clear-month">✕</button>
          )}
        </div>
      </div>

      {loading && <div className="progress-line mb-6" />}

      {stats && (
        <>
          {/* Global */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="global-stats">
            <StatCard label="Total dépenses" value={`${stats.global.total.toFixed(2)} $`} mono testId="stat-total" />
            <StatCard label="Factures" value={stats.global.count} testId="stat-count" />
            <StatCard label="Utilisateurs actifs" value={stats.global.user_count} testId="stat-users" />
            <StatCard label="Taxes TPS+TVQ+TVH" value={`${(stats.global.tps + stats.global.tvq + stats.global.tvh).toFixed(2)} $`} mono testId="stat-taxes" />
          </div>

          {/* Monthly trend chart */}
          <div className="ra-card p-4 sm:p-6 mb-6" data-testid="monthly-trend-chart">
            <div className="flex items-center gap-2 mb-5">
              <TrendingUp size={16} className="text-[var(--ra-primary)]" />
              <p className="overline">Tendance mensuelle</p>
            </div>
            {monthChart.length === 0 ? (
              <p className="text-sm text-[var(--ra-text-muted)] text-center py-10">Aucune donnée mensuelle</p>
            ) : (
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={monthChart} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--ra-primary)" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="var(--ra-primary)" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" stroke={axisColor} fontSize={11} />
                    <YAxis stroke={axisColor} fontSize={11} tickFormatter={(v) => `${v}$`} />
                    <Tooltip
                      contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 2, fontSize: 12 }}
                      formatter={(v, name) => name === "total" ? [`${Number(v).toFixed(2)} $`, "Total"] : [v, "Factures"]}
                      labelStyle={{ color: "var(--ra-text)", fontWeight: 600 }}
                    />
                    <Area type="monotone" dataKey="total" stroke="var(--ra-primary)" strokeWidth={2} fill="url(#totalGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Bar chart: users */}
            <div className="ra-card p-4 sm:p-6" data-testid="user-bar-chart">
              <div className="flex items-center gap-2 mb-5">
                <Users size={16} className="text-[var(--ra-primary)]" />
                <p className="overline">Top utilisateurs</p>
              </div>
              {userChart.length === 0 ? (
                <p className="text-sm text-[var(--ra-text-muted)] text-center py-10">Aucune donnée</p>
              ) : (
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={userChart} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke={axisColor} fontSize={11} tickFormatter={(v) => `${v}$`} />
                      <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={11} width={110} />
                      <Tooltip
                        contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 2, fontSize: 12 }}
                        formatter={(v) => [`${Number(v).toFixed(2)} $`, "Total"]}
                      />
                      <Bar dataKey="total" fill="var(--ra-primary)" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Bar chart: categories */}
            <div className="ra-card p-4 sm:p-6" data-testid="category-bar-chart">
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 size={16} className="text-[var(--ra-primary)]" />
                <p className="overline">Répartition par catégorie</p>
              </div>
              {categoryChart.length === 0 ? (
                <p className="text-sm text-[var(--ra-text-muted)] text-center py-10">Aucune donnée</p>
              ) : (
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={categoryChart} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid stroke={gridColor} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke={axisColor} fontSize={11} tickFormatter={(v) => `${v}$`} />
                      <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={11} width={130} />
                      <Tooltip
                        contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 2, fontSize: 12 }}
                        formatter={(v) => [`${Number(v).toFixed(2)} $`, "Total"]}
                      />
                      <Bar dataKey="total" fill="var(--ra-text)" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* By User */}
            <div className="ra-card p-6" data-testid="by-user-card">
              <div className="flex items-center gap-2 mb-5">
                <Users size={16} className="text-[#002FA7]" />
                <p className="overline">Par utilisateur</p>
              </div>
              <div className="space-y-3">
                {(stats.by_user || []).slice(0, 10).map((u) => (
                  <div key={u.user_id} data-testid={`user-bar-${u.user_id}`}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <div>
                        <span className="font-medium">{u.name}</span>
                        <span className="text-gray-400 ml-2 text-xs">({u.count} facture{u.count > 1 ? "s" : ""})</span>
                      </div>
                      <span className="num font-semibold">{u.total.toFixed(2)} $</span>
                    </div>
                    <div className="h-2 bg-gray-100 overflow-hidden">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${Math.max(2, (u.total / maxUser) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                {(!stats.by_user || stats.by_user.length === 0) && <p className="text-sm text-gray-400 text-center py-6">Aucune donnée</p>}
              </div>
            </div>

            {/* By Category */}
            <div className="ra-card p-6" data-testid="by-category-card">
              <div className="flex items-center gap-2 mb-5">
                <BarChart3 size={16} className="text-[#002FA7]" />
                <p className="overline">Par catégorie</p>
              </div>
              <div className="space-y-3">
                {(stats.by_category || []).map((c) => (
                  <div key={c.category} data-testid={`category-bar-${c.category}`}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <div>
                        <span className="font-medium">{CATEGORY_LABELS[c.category] || c.category}</span>
                        <span className="text-gray-400 ml-2 text-xs">({c.count})</span>
                      </div>
                      <span className="num font-semibold">{c.total.toFixed(2)} $</span>
                    </div>
                    <div className="h-2 bg-gray-100 overflow-hidden">
                      <div className="h-full bg-black" style={{ width: `${Math.max(2, (c.total / maxCat) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                {(!stats.by_category || stats.by_category.length === 0) && <p className="text-sm text-gray-400 text-center py-6">Aucune donnée</p>}
              </div>
            </div>
          </div>

          {/* By Month */}
          <div className="ra-card p-6" data-testid="by-month-card">
            <div className="flex items-center gap-2 mb-5">
              <TrendingUp size={16} className="text-[#002FA7]" />
              <p className="overline">Par mois</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(stats.by_month || []).map((m) => (
                <div key={m.month} className="border border-gray-200 p-3" data-testid={`month-${m.month}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="num font-heading font-bold text-lg tracking-tight">{m.month}</span>
                    <span className="text-xs text-gray-400">{m.count} facture{m.count > 1 ? "s" : ""}</span>
                  </div>
                  <div className="num font-heading font-black text-2xl text-[#002FA7] mb-2">{m.total.toFixed(2)} $</div>
                  <div className="h-1 bg-gray-100">
                    <div className="h-full bg-[#002FA7]" style={{ width: `${Math.max(3, (m.total / maxMonth) * 100)}%` }} />
                  </div>
                </div>
              ))}
              {(!stats.by_month || stats.by_month.length === 0) && <p className="col-span-full text-sm text-gray-400 text-center py-6">Aucune donnée</p>}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

function StatCard({ label, value, mono, testId }) {
  return (
    <div className="ra-card p-4" data-testid={testId}>
      <div className="overline mb-2">{label}</div>
      <div className={`text-3xl font-heading font-black tracking-tighter ${mono ? "num" : ""}`}>{value}</div>
    </div>
  );
}
